export type SignalsOnboardingStep = {
  id: string;
  anchor: string;
  title: string;
  body: string;
  example?: string;
  bullets?: string[];
  bulletsTitle?: string;
  sections?: Array<{ title: string; items: string[] }>;
  footerHint?: string;
  closing?: string;
  boldLine?: string;
};

export const buildSignalsOnboardingSteps = (
  _assetClassKey?: string,
): SignalsOnboardingStep[] => {
  return [
    {
      id: 'price-reference',
      anchor: 'price-reference',
      title: 'Start with Price Reference',
      body:
        'Anchored uses price as-of the last CFTC report (backtest-consistent). Live uses the latest price with the last known positioning.',
      closing: 'Use Live for monitoring, Anchored for historical context.',
    },
    {
      id: 'snapshot-filters',
      anchor: 'cftc-snapshot',
      title: 'Set the Snapshot Filters',
      body:
        'Choose the group and contract to define the CFTC report context. This drives the positioning data shown throughout the panel.',
      closing: 'Select the most relevant contract before interpreting signals.',
    },
    {
      id: 'regime',
      anchor: 'regime-badge',
      title: 'Identify the Regime',
      body:
        'Always read the regime badge first. It tells you what kind of market you’re in: Squeeze Watch, Flow-Driven, or Macro-Led. This tool is about context and asymmetry — not prediction.',
      footerHint: 'Rule: Regime → Alerts → Readiness → Trade plan.',
    },
    {
      id: 'squeeze-watch',
      anchor: 'regime-badge',
      title: '🟡 Squeeze Watch',
      body: 'Fast money is offsides; slow money is on the other side.',
      sections: [
        {
          title: 'What it means:',
          items: [
            'Leveraged Funds are meaningfully positioned the wrong way',
            'Asset Managers provide structural opposition',
            'If price moves against fast money, forced covering is possible',
          ],
        },
        {
          title: 'How to trade it:',
          items: [
            'Do not chase price',
            'Look for confirmation, not prediction',
            'Favor convex setups (enter on strength, or use options)',
          ],
        },
      ],
    },
    {
      id: 'flow-driven',
      anchor: 'regime-badge',
      title: '🔵 Flow-Driven',
      body: 'Positioning and price are moving together.',
      sections: [
        {
          title: 'What it means:',
          items: ['Positioning changes explain returns', 'Market is being pushed by flows, not stress'],
        },
        {
          title: 'How to trade it:',
          items: [
            'Momentum and trend-following are valid',
            'Less need to fade or anticipate reversals',
          ],
        },
      ],
    },
    {
      id: 'macro-led',
      anchor: 'regime-badge',
      title: '⚪ Macro-Led / Uncorrelated',
      body: 'Positioning is not explaining price.',
      sections: [
        {
          title: 'What it means:',
          items: ['CFTC data is context only', 'Macro, rates, or spot flows dominate'],
        },
        {
          title: 'How to trade it:',
          items: [
            'Use positioning defensively (risk framing)',
            'Do not time trades from CFTC alone',
          ],
        },
      ],
    },
    {
      id: 'alerts',
      anchor: 'alerts-pill',
      title: 'Alerts = What Changed',
      body:
        'Alerts notify you when the state shifts. They don’t tell you what to trade — they tell you when risk is changing.',
      bullets: [
        'Setup Detected: pay attention',
        'Risk Rising: fragility building',
        'Trigger: execution window',
        'Cover Risk: manage shorts',
        'Crowding: don’t chase',
      ],
    },
    {
      id: 'readiness',
      anchor: 'readiness-summary',
      title: 'Trade Readiness = Setup Quality',
      body:
        'Readiness scores how close you are to a tradeable event. It’s not a forecast. Use it to decide whether to watch, prepare, or execute.',
      bulletsTitle: 'Scale:',
      bullets: [
        '0–39 Not Ready (context only)',
        '40–59 Watch (forming)',
        '60–79 High Readiness (prepare)',
        '80–100 Triggered (selective execution)',
      ],
    },
    {
      id: 'components',
      anchor: 'readiness-breakdown',
      title: 'What the Components Mean',
      body: 'What each component tells you',
      bulletsTitle: 'Each component maps to a trader question:',
      bullets: [
        'Offsides: who is trapped?',
        'Participation: is fresh risk entering (OI)?',
        'Pressure: is positioning building against price?',
        'Trigger: has price confirmed (breakout/MA)?',
      ],
      sections: [
        {
          title: 'Offsides',
          items: ['Who is trapped?', 'High = one side is vulnerable', 'Low = no forced behavior expected'],
        },
        {
          title: 'Participation',
          items: ['Is fresh risk entering?', 'High = moves can extend', 'Low = moves may fade'],
        },
        {
          title: 'Pressure',
          items: ['Is positioning building against price?', 'High = fragility increasing', 'Low = no stress yet'],
        },
        {
          title: 'Trigger',
          items: ['Has price confirmed?', 'High = catalyst active', 'Low = wait'],
        },
      ],
      closing:
        'High Offsides + High Trigger = squeeze risk active. Low Offsides + High Trigger = flow move, not a squeeze.',
    },
    {
      id: 'combine',
      anchor: 'readiness-breakdown',
      title: 'How professionals combine them',
      body: '',
      bullets: [
        'High Offsides + Low Trigger → Setup building, wait for confirmation',
        'High Offsides + High Trigger → Squeeze risk active',
        'Low Offsides + High Trigger → Flow-driven move, not a squeeze',
        'High Trigger + Falling Offsides → Late move, manage risk',
      ],
    },
    {
      id: 'golden-rules',
      anchor: 'readiness-summary',
      title: 'Golden Rules',
      body: 'Use signals for structure and risk framing — not timing.',
      bullets: [
        'Don’t trade extremes without confirmation',
        'Don’t short into trapped shorts',
        'CFTC is weekly — avoid intraday conclusions',
      ],
      boldLine: 'Positioning tells you who may be forced next — price tells you when.',
    },
  ];
};

export const buildCftcSummaryOnboardingSteps = (): SignalsOnboardingStep[] => [
  {
    id: 'header',
    anchor: 'cftc-snapshot-header',
    title: 'Start With the Question',
    body:
      'This view answers one question: Who is positioned, how crowded is it, and is that positioning changing? It does not predict price. It defines macro context.',
  },
  {
    id: 'selectors',
    anchor: 'cftc-market-selectors',
    title: 'Select the Correct Market',
    body:
      'Choose the exact futures market you are analyzing. CFTC data is contract-specific — wrong contract = wrong conclusions. Tip: FX spot traders should still use the CME futures equivalent for positioning context.',
  },
  {
    id: 'report-date',
    anchor: 'cftc-report-date',
    title: 'Anchor to the Report Date',
    body:
      'CFTC positioning is weekly. All analysis on this page is anchored to the selected report date. Warning: Do not compare this directly to intraday price without accounting for the time lag.',
  },
  {
    id: 'oi-distribution',
    anchor: 'cftc-oi-distribution',
    title: 'Open Interest Distribution (Pie)',
    body:
      'This shows who controls the open interest — not direction, but participation. Asset Managers → structural. Leveraged Funds → fast directional. Dealers → hedging/liquidity. Large concentration = crowding risk, not conviction.',
  },
  {
    id: 'position-snapshot',
    anchor: 'cftc-position-snapshot',
    title: 'Gross Positioning (Positions by participant)',
    body:
      'These bars show gross positioning by participant: Long, Short, Spreading. Key rule: Directional bias comes from net, not gross size.',
  },
  {
    id: 'net-positioning',
    anchor: 'cftc-net-positioning',
    title: 'Net Is the Signal (Table: Net column)',
    body:
      'Net positioning reveals true directional exposure. This is where bias begins — not entries. Look for: expanding net (conviction), flat net (churn), extreme net (squeeze risk).',
  },
  {
    id: 'oi-percent-donuts',
    anchor: 'cftc-oi-percent-donuts',
    title: 'Normalize With % of Open Interest (Donuts)',
    body:
      'Percent of open interest normalizes positioning across time and regimes. Extremes here matter more than raw contract counts.',
  },
  {
    id: 'change-contracts',
    anchor: 'cftc-change-contracts',
    title: 'Weekly Change (Diverging Bar)',
    body:
      'This shows weekly positioning change, not level. Adding in trend can fuel continuation. Adding against price can build pressure. Reducing during breakout often signals flow-driven movement.',
  },
  {
    id: 'waterfall',
    anchor: 'cftc-waterfall-net-change',
    title: 'Who Drove the Move (Waterfall)',
    body:
      'This summarizes who drove the net change this week. Single-group dominance is fragile. Multi-group alignment is more durable.',
  },
  {
    id: 'table',
    anchor: 'cftc-participant-table',
    title: 'Audit Trail',
    body:
      'This table is your audit trail. Use it to validate charts, cross-check net vs gross, and export for journaling or review.',
  },
  {
    id: 'disclaimer',
    anchor: 'cftc-disclaimer',
    title: 'What This View Is (and Isn’t)',
    body:
      '✔ Defines macro regime ✔ Identifies crowding and pressure ✔ Explains why price may behave a certain way ✖ Does not time entries ✖ Does not generate signals ✖ Is not real-time',
  },
];
